import { Field, TextInput, toast } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import {
  createDatasetFolder,
  deleteDatasetFolder,
  updateDatasetFolder,
  type DatasetFolderNode,
  type DatasetFolderTree,
} from "@/api/datasetFolder";
import type { UserRole } from "@/app";
import { HatTextModal } from "@/components/modal";
import { resolveLoadErrorMessage } from "../loadState";
import { FolderSelectField, noFolderValue } from "./FolderSelectField";
import { flattenFolderTree } from "./folderTree";

// 레인이 여는 폴더 쓰기 창. `node` 는 대상 노드이고, `parentUuid` 는 새로 만들 위치다.
export type FolderDialogRequest =
  | { kind: "create"; parentUuid: string | null }
  | { kind: "rename"; node: DatasetFolderNode }
  | { kind: "move"; node: DatasetFolderNode }
  // `datasetCount` 는 삭제로 미분류가 되는 Dataset 수다. 트리의 건수가 저장 위치로 좁혀져 있으면
  // null 이다. 삭제는 모든 저장 위치의 Dataset 을 미분류로 보낸다. 좁힌 수는 그 영향보다 작을 수 있다.
  | { kind: "delete"; node: DatasetFolderNode; datasetCount: number | null };

export type DatasetFolderDialogsProps = {
  request: FolderDialogRequest | null;
  tree: DatasetFolderTree;
  currentUserRole: UserRole | null;
  onClose: () => void;
  // 쓰기가 성공하면 호출한다. 트리와 목록을 다시 조회한다(건수가 바뀐다).
  onChanged: () => void;
};

/**
 * 폴더 트리 쓰기 창 넷(만들기·이름 변경·옮기기·삭제)을 한 곳에서 소유한다.
 *
 * 레인은 요청만 보내고, 창은 이 컴포넌트가 소유한다. 레인은 스크롤 컨테이너 안에 있다. 창을
 * 그 안에 두면 행이 사라질 때(삭제 성공) 창도 함께 언마운트된다.
 */
export function DatasetFolderDialogs({
  request,
  tree,
  currentUserRole,
  onClose,
  onChanged,
}: DatasetFolderDialogsProps): ReactNode {
  const [name, setName] = useState("");
  const [parentValue, setParentValue] = useState(noFolderValue);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 언마운트 뒤 도착한 응답이 상태를 바꾸지 않게 하는 가드. 토스트는 결과 통지이므로 그대로 띄운다.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // 창이 열릴 때마다 대상에서 초기값을 다시 읽는다. 이전 대상의 입력이 남으면 다른 폴더의
  // 이름으로 확정하게 된다.
  useEffect(() => {
    setErrorMessage(null);
    if (request === null) {
      return;
    }
    setName(request.kind === "rename" ? request.node.display_name : "");
    setParentValue(
      request.kind === "create" && request.parentUuid !== null ? request.parentUuid : noFolderValue,
    );
  }, [request]);

  if (request === null) {
    return null;
  }

  const options = { role: currentUserRole };
  const finish = (message: string): void => {
    toast.success(message);
    onChanged();
    onClose();
  };
  const fail = (error: unknown): void => {
    if (aliveRef.current) {
      setErrorMessage(resolveLoadErrorMessage(error));
    }
  };
  const run = (work: Promise<unknown>, message: string): void => {
    setBusy(true);
    setErrorMessage(null);
    void work
      .then(() => finish(message))
      .catch(fail)
      .finally(() => {
        if (aliveRef.current) setBusy(false);
      });
  };

  if (request.kind === "delete") {
    return (
      <DeleteFolderDialog
        node={request.node}
        datasetCount={request.datasetCount}
        busy={busy}
        errorMessage={errorMessage}
        onCancel={onClose}
        onConfirm={() =>
          run(
            deleteDatasetFolder(request.node.uuid, options),
            `'${request.node.display_name}' 폴더를 삭제했습니다.`,
          )
        }
      />
    );
  }

  if (request.kind === "move") {
    // 자기 자신은 부모가 될 수 없다. 자식이 있는 폴더는 다른 폴더 아래로 이동할 수 없다.
    // 그 자식이 3계층이 되기 때문이다. 서버가 422 로 막는 배치를 화면에서 먼저 잠근다.
    const movingHasChildren = request.node.children.length > 0;
    return (
      <HatTextModal
        open
        label="폴더 옮기기"
        eyebrow={`폴더 · ${request.node.display_name}`}
        size="sm"
        busy={busy}
        errorMessage={errorMessage ?? undefined}
        okText="옮기기"
        onCancelPress={onClose}
        onOkPress={() =>
          run(
            updateDatasetFolder(
              request.node.uuid,
              { parent_uuid: parentValue === noFolderValue ? null : parentValue },
              options,
            ),
            `'${request.node.display_name}' 폴더를 옮겼습니다.`,
          )
        }
        caption={
          movingHasChildren
            ? "하위 폴더가 있어 루트로만 둘 수 있습니다. 폴더는 두 계층까지입니다."
            : "옮길 위치를 고릅니다. 폴더는 두 계층까지입니다."
        }
        content={
          <FolderSelectField
            tree={tree}
            label="상위 폴더"
            value={parentValue}
            onValueChange={setParentValue}
            noFolderLabel="루트로 꺼내기"
            excludeUuids={new Set([request.node.uuid])}
            disabled={busy}
            disabledReason={(_uuid, depth) => {
              if (depth > 0) {
                return "이미 2계층이라 그 밑에 넣을 수 없습니다.";
              }
              return movingHasChildren ? "하위 폴더가 있어 남의 밑으로 넣을 수 없습니다." : null;
            }}
          />
        }
      />
    );
  }

  const isCreate = request.kind === "create";
  const trimmed = name.trim();
  return (
    <HatTextModal
      open
      label={isCreate ? "새 폴더" : "폴더 이름 변경"}
      eyebrow={isCreate ? undefined : `폴더 · ${request.node.display_name}`}
      size="sm"
      busy={busy}
      okDisabled={trimmed === ""}
      errorMessage={errorMessage ?? undefined}
      okText={isCreate ? "만들기" : "저장"}
      onCancelPress={onClose}
      onOkPress={() =>
        isCreate
          ? run(
              createDatasetFolder(
                {
                  display_name: trimmed,
                  parent_uuid: parentValue === noFolderValue ? null : parentValue,
                },
                options,
              ),
              `'${trimmed}' 폴더를 만들었습니다.`,
            )
          : run(
              // 이름만 바꾼다. `parent_uuid` 를 함께 보내면 의도하지 않은 이동이 된다
              // (서버는 exclude_unset 으로 읽으므로 키를 생략하면 "부모 유지" 다).
              updateDatasetFolder(request.node.uuid, { display_name: trimmed }, options),
              `폴더 이름을 '${trimmed}' 로 바꿨습니다.`,
            )
      }
      content={
        <>
          <Field label="폴더 이름" layout="stack">
            <TextInput
              value={name}
              disabled={busy}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
            />
          </Field>
          {isCreate ? (
            <FolderSelectField
              tree={tree}
              label="상위 폴더"
              value={parentValue}
              onValueChange={setParentValue}
              noFolderLabel="루트에 만들기"
              disabled={busy}
              disabledReason={(_uuid, depth) =>
                depth > 0 ? "이미 2계층이라 그 밑에 만들 수 없습니다." : null
              }
            />
          ) : null}
        </>
      }
    />
  );
}

// 삭제 확인. 영향을 수로 표시한다. 자식 폴더 수와 미분류로 갈 데이터셋 수는 트리 응답에
// 이미 있다(노드의 dataset_count 가 자손 포함). 그래서 추가 조회가 없다. 그 수를 모르면
// (`datasetCount` 가 null) 수 없이 모두 미분류로 간다고 말한다.
function DeleteFolderDialog({
  node,
  datasetCount,
  busy,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  node: DatasetFolderNode;
  datasetCount: number | null;
  busy: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}): ReactNode {
  const childCount = node.children.length;
  const parts = [
    childCount > 0 ? `하위 폴더 ${childCount}개가 함께 사라지고` : null,
    datasetCount === null
      ? "이 폴더의 Dataset은 모두 미분류로 갑니다"
      : datasetCount > 0
        ? `Dataset ${datasetCount}개가 미분류로 갑니다`
        : "이 폴더에 든 Dataset은 없습니다",
  ].filter((part) => part !== null);

  return (
    <HatTextModal
      open
      error
      label="폴더 삭제"
      eyebrow={`폴더 · ${node.display_name}`}
      size="sm"
      busy={busy}
      errorMessage={errorMessage ?? undefined}
      okText="삭제"
      onCancelPress={onCancel}
      onOkPress={onConfirm}
      caption={`${parts.join(", ")}. Dataset 자체는 지워지지 않습니다.`}
    />
  );
}

// 옮길 폴더 목록이 비었는지 판정한다. 호출부가 창을 열기 전에 확인할 수 있게 export 한다.
export function hasAnyFolder(tree: DatasetFolderTree): boolean {
  return flattenFolderTree(tree).length > 0;
}
